import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  IconButton,
  createTheme,
  ThemeProvider,
  Container,
  Grid,
} from '@mui/material';
import {
  Business as BusinessIcon,
  ArrowBack as ArrowBackIcon,
  Brightness4 as Brightness4Icon,
  Brightness7 as Brightness7Icon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import axios from 'axios';
import SuccessDialog from '../components/SuccessDialog';
import { useSharedTheme } from '../theme/useSharedTheme';
import { getApiUrl } from '../utils/apiUrl';

const ClientSignup: React.FC = () => {
  const navigate = useNavigate();
  const { isDarkMode, toggleTheme, theme } = useSharedTheme();
  const [explicitSignupAccess, setExplicitSignupAccess] = useState(false);
  const hasProcessedExplicitAccess = useRef(false);
  
  
  // Check for explicit signup access on component mount and handle redirects
  useEffect(() => {
    // If we've already processed explicit access, don't run again
    if (hasProcessedExplicitAccess.current) {
      return;
    }
    
    const isExplicitAccess = sessionStorage.getItem('explicitSignupAccess');
    
    if (isExplicitAccess) {
      setExplicitSignupAccess(true);
      sessionStorage.removeItem('explicitSignupAccess');
      hasProcessedExplicitAccess.current = true;
      // Don't redirect if we have explicit access
      return;
    }
    
    // Only check for redirect if we don't have explicit access
    const savedLoginUrl = sessionStorage.getItem('lastLoginUrl');
    const logoutInProgress = sessionStorage.getItem('logoutInProgress');
    
    if (savedLoginUrl && !logoutInProgress) {
      window.location.href = savedLoginUrl;
    }
  }, []);
  
  // Extract company name from URL path
  const getCompanyNameFromUrl = () => {
    const pathParts = window.location.pathname.split('/');
    const companySlug = pathParts[1];
    
    // Check if we're in a company context (not admin, client, etc.)
    if (companySlug && !['admin', 'client', 'account-deactivated', 'admin-signup', 'client-signup', 'reset-password'].includes(companySlug)) {
      return companySlug;
    }
    
    return 'Unknown Company';
  };
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [industry, setIndustry] = useState('');
  const [phone, setPhone] = useState('');
  const [businessInfo, setBusinessInfo] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [successDialogConfig, setSuccessDialogConfig] = useState({
    title: 'Request Submitted Successfully',
    message: "Admin request submitted. We'll be in touch soon.",
    autoCloseDelay: 5000,
  });
  
  // Auto-dismiss dialog after 5 seconds
  React.useEffect(() => {
    if (showSuccessDialog) {
      const timer = setTimeout(() => {
        setShowSuccessDialog(false);
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [showSuccessDialog]);
  
  // CRITICAL: Check if logout is in progress to prevent this component from being an intermediate redirect target
  React.useEffect(() => {
    const logoutInProgress = sessionStorage.getItem('logoutInProgress');
    if (logoutInProgress) {
      console.log('[DEBUG] ClientSignup: Logout in progress, preventing render');
      return; // Don't render anything while logout is processing
    }
  }, []);

  // CRITICAL: Check if there's a force redirect flag set during logout
  const forceRedirect = sessionStorage.getItem('forceRedirect');
  if (forceRedirect) {
    console.log('[DEBUG] ClientSignup: Force redirect detected, redirecting to:', forceRedirect);
    sessionStorage.removeItem('forceRedirect');
    window.location.href = forceRedirect;
  }

  // Get the last login URL or detect company context
  const getBackToLoginUrl = () => {
    // First, try to get the last login URL from sessionStorage
    const lastLoginUrl = sessionStorage.getItem('lastLoginUrl');
    if (lastLoginUrl) {
      return lastLoginUrl;
    }
    
    // Try to get company slug from sessionStorage first, then localStorage
    const savedCompanySlug = sessionStorage.getItem('companySlug') || localStorage.getItem('companySlug');
    if (savedCompanySlug) {
      // Clean up localStorage after use
      localStorage.removeItem('companySlug');
      return `/${savedCompanySlug}/login`;
    }
    
    // If no saved company slug, try to detect company context from current URL
    const pathParts = window.location.pathname.split('/');
    const companySlug = pathParts[1];
    
    // Check if we're in a company context (not admin, client, etc.)
    if (companySlug && !['admin', 'client', 'account-deactivated', 'admin-signup', 'client-signup', 'reset-password'].includes(companySlug)) {
      return `/${companySlug}/login`;
    }
    
    // Fallback to admin login if no company context
    return '/admin/login';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email.trim() || !fullName.trim() || !businessInfo.trim()) {
      setSuccessDialogConfig({
        title: 'Missing Information',
        message: 'Please fill in required fields',
        autoCloseDelay: 3000,
      });
      setShowSuccessDialog(true);
      return;
    }

    setSubmitting(true);
    try {
      const clientRequestMessage = `CLIENT ACCOUNT REQUEST
      
Client Full Name: ${fullName.trim()}
Business Email: ${email.trim()}
Company Name: ${companyName.trim() || 'Not provided'}
Business Type: ${industry.trim() || 'Not specified'}
Phone: ${phone.trim() || 'Not provided'}

Business Requirements & Goals:
${businessInfo.trim()}`;

      await axios.post(`${getApiUrl()}/api/support/inquiry`, {
        email: email.trim(),
        message: clientRequestMessage,
        type: 'signup',
        metadata: {
          accountType: 'client'
        },
        companyName: getCompanyNameFromUrl(),
        clientName: fullName.trim(),
        businessInfo: businessInfo.trim()
      });

      // Show success dialog instead of snackbar
      setShowSuccessDialog(true);
      
      // Clear form
      setEmail('');
      setFullName('');
      setCompanyName('');
      setIndustry('');
      setPhone('');
      setBusinessInfo('');
    } catch (error: any) {
      console.error('Client signup request error:', error);
      setSuccessDialogConfig({
        title: 'Submission Failed',
        message: error.response?.data?.message || 'Failed to submit request. Please try again.',
        autoCloseDelay: 3000,
      });
      setShowSuccessDialog(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          display: 'flex',
          minHeight: '100vh',
          height: { xs: 'auto', md: '100vh' },
          overflow: { xs: 'auto', md: 'hidden' },
          background: { 
            xs: 'transparent', 
            md: isDarkMode 
              ? theme.palette.gradients.authDark
              : theme.palette.gradients.authLight
          },
          transition: 'background 0.5s ease-in-out',
          position: 'relative',
          fontFamily: '"Nunito Sans", sans-serif',
        }}
      >
        {/* Centered Form Container */}
        <Box
          sx={{
            display: 'flex',
            alignItems: { xs: 'flex-start', md: 'center' },
            justifyContent: 'center',
            width: '100%',
            minHeight: '100vh',
            height: { xs: 'auto', md: '100vh' },
            padding: { xs: 0, md: 4 },
            paddingTop: 0,
            '&.css-1tm6nwn': {
              padding: 0,
            },
          }}
        >
          <Box 
            sx={{
              width: { xs: '100%', md: '80%' }, 
              maxWidth: { xs: 'none', md: 1000 }, 
              backgroundColor: isDarkMode ? '#2e2e30' : '#ffffff',
              border: { xs: 'none', md: isDarkMode ? '0.3% solid #B9B9B9' : '0.3px solid #B9B9B9' },
              borderRadius: { xs: 0, md: 4 },
              padding: { xs: '10px', md: 4 },
              marginBottom: { xs: 0, md: 0 },
              boxShadow: { xs: 'none', md: '0 20px 40px rgba(0, 0, 0, 0.1)' },
              position: 'relative',
              display: 'flex', 
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: { xs: '100vh', md: '670px' },
              transition: 'background-color 0.3s ease',
              '&.css-1br60pi': {
                width: '100%',
                maxWidth: 'none',
                border: 'none',
                borderRadius: 0,
                padding: '24px',
                marginBottom: '0px',
                boxShadow: 'none',
                flexDirection: 'column',
                minHeight: '100vh',
              },
            }}
          >
            {/* Back Button - Top Left Corner of White Container */}
            <Box
              sx={{
                position: 'absolute',
                top: '2%',
                left: '4%',
                display: 'none',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'transparent',
                border: isDarkMode ? '1px solid #ffffff' : '1px solid #000000',
                borderRadius: 20,
                padding: { xs: 1.5, md: 2 },
                width: { xs: 40, md: 40 },
                height: { xs: 40, md: 40 },
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                zIndex: 1000,
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                }
              }}
              onClick={() => navigate(getBackToLoginUrl())}
            >
              <ArrowBackIcon
                sx={{
                  color: isDarkMode ? '#ffffff' : '#1B4672',
                  fontSize: '20px',
                  transition: 'color 0.3s ease',
                }}
              />
            </Box>

            {/* Theme Toggle - Top Right Corner of Container */}
            <Box
              sx={{
                position: 'absolute',
                top: '1%',
                right: '1%',
                display: { xs: 'none', md: 'none' },
                alignItems: 'center',
                backgroundColor: 'transparent',
                border: isDarkMode ? '1px solid #ffffff' : '1px solid #000000',
                borderRadius: 20,
                padding: { xs: 1.5, md: 2 },
                width: { xs: 60, md: 80 },
                height: { xs: 32, md: 40 },
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                zIndex: 1000,
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                }
              }}
              onClick={toggleTheme}
            >
              {/* Sun Icon - Left Side */}
              <Box
                sx={{
                  position: 'absolute',
                  left: { xs: 3, md: 4 },
                  top: '50%',
                  transform: 'translateY(-50%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: { xs: 20, md: 24 },
                  height: { xs: 20, md: 24 },
                  zIndex: 2,
                }}
              >
                <Brightness7Icon 
                  sx={{ 
                    fontSize: { xs: 16, md: 20 },
                    color: isDarkMode ? '#ffffff' : '#000000',
                    transition: 'all 0.3s ease',
                    opacity: isDarkMode ? 0.3 : 1,
                  }} 
                />
              </Box>
              
              {/* Moon Icon - Right Side */}
              <Box
                sx={{
                  position: 'absolute',
                  right: { xs: 3, md: 4 },
                  top: '50%',
                  transform: 'translateY(-50%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: { xs: 20, md: 24 },
                  height: { xs: 20, md: 24 },
                  zIndex: 2,
                }}
              >
                <Brightness4Icon 
                  sx={{ 
                    fontSize: { xs: 16, md: 20 },
                    color: isDarkMode ? '#ffffff' : '#000000',
                    transition: 'all 0.3s ease',
                    opacity: isDarkMode ? 1 : 0.3,
                  }} 
                />
              </Box>
              
              {/* Toggle Switch */}
              <Box
                sx={{
                  position: 'absolute',
                  top: { xs: 2, md: 4 },
                  left: isDarkMode ? { xs: 2, md: 4 } : { xs: 30, md: 44 },
                  width: { xs: 28, md: 32 },
                  height: { xs: 28, md: 32 },
                  background: isDarkMode ? '#ffffff' : '#1B4672',
                  borderRadius: '50%',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                  transition: 'left 0.3s ease, background 0.3s ease',
                  zIndex: 3,
                }}
              />
            </Box>


            {/* Heading */}
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: isDarkMode ? '#ffffff' : '#202224',
                mb: 2,
                textAlign: 'center',
                fontSize: { xs: '1.5rem', md: '2rem' },
                letterSpacing: '-0.11px',
              }}
            >
              Request Client Account
            </Typography>
            
            {/* Description */}
            <Typography
              variant="body1"
              sx={{
                color: isDarkMode ? '#ffffff' : '#666666',
                mb: 4,
                textAlign: 'center',
                fontSize: { xs: '0.9rem', md: '16px' },
                letterSpacing: '-0.06px',
                opacity: 0.8,
                maxWidth: '700px',
                lineHeight: 1.4,
              }}
            >
              <Box component="span" sx={{ display: { xs: 'none', md: 'inline' } }}>
                Sign up for LeadForge! Submit your business info, and we'll create your account.
              </Box>
              <Box component="span" sx={{ display: { xs: 'inline', md: 'none' } }}>
                Sign up for LeadForge! Submit your business info, and we'll create your account.
              </Box>
            </Typography>

            {/* Form */}
            <Box sx={{ width: '100%', maxWidth: '800px' }}>
              <form onSubmit={handleSubmit}>
                {/* Two Column Layout */}
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 3, md: 4 }, mb: 4, alignItems: { xs: 'stretch', md: 'flex-start' } }}>
                  {/* Left Column */}
                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minHeight: { xs: 'auto', md: '320px' } }}>
                    <Box>
                      <Box sx={{ mb: 3 }}>
                        <TextField
                          fullWidth
                          placeholder="Client Full Name *"
                          value={fullName}
                          onChange={(e) => setFullName(e.target.value)}
                          required
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '30px',
                              backgroundColor: `${isDarkMode ? '#2e2e30' : '#f8fafc'}`,
                              border: `${isDarkMode ? 'none' : '1px solid #e2e8f0'}`,
                              '& fieldset': { border: 'none', display: 'none' },
                              '& input': {
                                color: `${isDarkMode ? '#A6A6A6' : '#202224'}`,
                                fontSize: '1rem',
                                padding: '12px 16px',
                                backgroundColor: 'transparent',
                              },
                              '& input::placeholder': {
                                color: `${isDarkMode ? '#A6A6A6' : '#999999'}`,
                                opacity: '1',
                              },
                              '&:hover': {
                                backgroundColor: `${isDarkMode ? '#2e2e30' : '#f1f5f9'}`,
                                border: `${isDarkMode ? 'none' : '1px solid #cbd5e1'}`,
                                '& fieldset': {
                                  border: 'none',
                                  display: 'none',
                                },
                              },
                              '&.Mui-focused': {
                                backgroundColor: `${isDarkMode ? '#2e2e30' : '#ffffff'}`,
                                border: '1px solid #6052A2',
                                boxShadow: '0 0 0 3px rgba(96, 82, 162, 0.1)',
                                '& fieldset': {
                                  border: 'none',
                                  display: 'none',
                                },
                              },
                            },
                          }}
                        />
                      </Box>

                      <Box sx={{ mb: 3 }}>
                        <TextField
                          fullWidth
                          placeholder="Company Name *"
                          value={companyName}
                          onChange={(e) => setCompanyName(e.target.value)}
                          required
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '30px',
                              backgroundColor: `${isDarkMode ? '#2e2e30' : '#f8fafc'}`,
                              border: `${isDarkMode ? 'none' : '1px solid #e2e8f0'}`,
                              '& fieldset': { border: 'none', display: 'none' },
                              '& input': {
                                color: `${isDarkMode ? '#A6A6A6' : '#202224'}`,
                                fontSize: '1rem',
                                padding: '12px 16px',
                                backgroundColor: 'transparent',
                              },
                              '& input::placeholder': {
                                color: `${isDarkMode ? '#A6A6A6' : '#999999'}`,
                                opacity: '1',
                              },
                              '&:hover': {
                                backgroundColor: `${isDarkMode ? '#2e2e30' : '#f1f5f9'}`,
                                border: `${isDarkMode ? 'none' : '1px solid #cbd5e1'}`,
                                '& fieldset': {
                                  border: 'none',
                                  display: 'none',
                                },
                              },
                              '&.Mui-focused': {
                                backgroundColor: `${isDarkMode ? '#2e2e30' : '#ffffff'}`,
                                border: '1px solid #6052A2',
                                boxShadow: '0 0 0 3px rgba(96, 82, 162, 0.1)',
                                '& fieldset': {
                                  border: 'none',
                                  display: 'none',
                                },
                              },
                            },
                          }}
                        />
                      </Box>

                      <Box sx={{ mb: 3 }}>
                        <TextField
                          fullWidth
                          type="email"
                          placeholder="Business Email Address *"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          required
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '30px',
                              backgroundColor: `${isDarkMode ? '#2e2e30' : '#f8fafc'}`,
                              border: `${isDarkMode ? 'none' : '1px solid #e2e8f0'}`,
                              '& fieldset': { border: 'none', display: 'none' },
                              '& input': {
                                color: `${isDarkMode ? '#A6A6A6' : '#202224'}`,
                                fontSize: '1rem',
                                padding: '12px 16px',
                                backgroundColor: 'transparent',
                              },
                              '& input::placeholder': {
                                color: `${isDarkMode ? '#A6A6A6' : '#999999'}`,
                                opacity: '1',
                              },
                              '&:hover': {
                                backgroundColor: `${isDarkMode ? '#2e2e30' : '#f1f5f9'}`,
                                border: `${isDarkMode ? 'none' : '1px solid #cbd5e1'}`,
                                '& fieldset': {
                                  border: 'none',
                                  display: 'none',
                                },
                              },
                              '&.Mui-focused': {
                                backgroundColor: `${isDarkMode ? '#2e2e30' : '#ffffff'}`,
                                border: '1px solid #6052A2',
                                boxShadow: '0 0 0 3px rgba(96, 82, 162, 0.1)',
                                '& fieldset': {
                                  border: 'none',
                                  display: 'none',
                                },
                              },
                            },
                          }}
                        />
                      </Box>

                      <Box sx={{ mb: 3 }}>
                        <TextField
                          fullWidth
                          placeholder="Phone Number"
                          value={phone}
                          onChange={(e) => setPhone(e.target.value)}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '30px',
                              backgroundColor: `${isDarkMode ? '#2e2e30' : '#f8fafc'}`,
                              border: `${isDarkMode ? 'none' : '1px solid #e2e8f0'}`,
                              '& fieldset': { border: 'none', display: 'none' },
                              '& input': {
                                color: `${isDarkMode ? '#A6A6A6' : '#202224'}`,
                                fontSize: '1rem',
                                padding: '12px 16px',
                                backgroundColor: 'transparent',
                              },
                              '& input::placeholder': {
                                color: `${isDarkMode ? '#A6A6A6' : '#999999'}`,
                                opacity: '1',
                              },
                              '&:hover': {
                                backgroundColor: `${isDarkMode ? '#2e2e30' : '#f1f5f9'}`,
                                border: `${isDarkMode ? 'none' : '1px solid #cbd5e1'}`,
                                '& fieldset': {
                                  border: 'none',
                                  display: 'none',
                                },
                              },
                              '&.Mui-focused': {
                                backgroundColor: `${isDarkMode ? '#2e2e30' : '#ffffff'}`,
                                border: '1px solid #6052A2',
                                boxShadow: '0 0 0 3px rgba(96, 82, 162, 0.1)',
                                '& fieldset': {
                                  border: 'none',
                                  display: 'none',
                                },
                              },
                            },
                          }}
                        />
                      </Box>
                    </Box>

                    <Box>
                      <TextField
                        fullWidth
                        placeholder="Business Type / Industry"
                        value={industry}
                        onChange={(e) => setIndustry(e.target.value)}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '30px',
                            backgroundColor: `${isDarkMode ? '#2e2e30' : '#f8fafc'}`,
                            border: `${isDarkMode ? 'none' : '1px solid #e2e8f0'}`,
                            '& fieldset': { border: 'none', display: 'none' },
                            '& input': {
                              color: `${isDarkMode ? '#A6A6A6' : '#202224'}`,
                              fontSize: '1rem',
                              padding: '12px 16px',
                              backgroundColor: 'transparent',
                            },
                          },
                        }}
                      />
                    </Box>
                  </Box>

                  {/* Right Column */}
                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', minHeight: { xs: 'auto', md: '320px' } }}>
                    <TextField
                      fullWidth
                      multiline
                      rows={6}
                      placeholder="Business Requirements & Goals *"
                      value={businessInfo}
                      onChange={(e) => setBusinessInfo(e.target.value)}
                      required
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '30px',
                          backgroundColor: `${isDarkMode ? '#2e2e30' : '#f8fafc'}`,
                          border: `${isDarkMode ? 'none' : '1px solid #e2e8f0'}`,
                          '& fieldset': { border: 'none', display: 'none' },
                          '& textarea': {
                            color: `${isDarkMode ? '#A6A6A6' : '#202224'}`,
                            fontSize: '1rem',
                            padding: '12px 16px',
                            backgroundColor: 'transparent',
                          },
                          '& textarea::placeholder': {
                            color: `${isDarkMode ? '#A6A6A6' : '#999999'}`,
                            opacity: '1',
                          },
                          '&:hover': {
                            backgroundColor: `${isDarkMode ? '#2e2e30' : '#f1f5f9'}`,
                            border: `${isDarkMode ? 'none' : '1px solid #cbd5e1'}`,
                            '& fieldset': {
                              border: 'none',
                              display: 'none',
                            },
                          },
                          '&.Mui-focused': {
                            backgroundColor: `${isDarkMode ? '#2e2e30' : '#ffffff'}`,
                            border: '1px solid #6052A2',
                            boxShadow: '0 0 0 3px rgba(96, 82, 162, 0.1)',
                            '& fieldset': {
                              border: 'none',
                              display: 'none',
                            },
                          },
                        },
                      }}
                    />
                  </Box>
                </Box>

                {/* Submit Button */}
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={submitting}
                    sx={{
                      py: 1.5,
                      px: 6,
                      borderRadius: 2,
                      background: isDarkMode 
                        ? '#ffffff' 
                        : '#1B4672',
                      textTransform: 'none',
                      fontSize: '1rem',
                      fontWeight: 600,
                      color: isDarkMode ? '#1B4672' : 'white',
                      '&:hover': {
                        background: isDarkMode 
                          ? '#f0f0f0' 
                          : '#1B4672',
                        transform: 'translateY(-1px)',
                      },
                      transition: 'all 0.3s ease-in-out',
                    }}
                  >
                    {submitting ? 'Submitting Request...' : 'Request Client Account'}
                  </Button>
                </Box>
              </form>

              {/* Back to Login */}
              <Typography
                variant="body2"
                sx={{
                  textAlign: 'center',
                  mt: 0,
                  color: isDarkMode ? '#ffffff' : '#666666',
                }}
              >
                <Button
                  variant="text"
                  onClick={() => navigate(getBackToLoginUrl())}
                  sx={{
                    color: isDarkMode ? '#ffffff' : '#1B4672',
                    textDecoration: 'underline',
                    fontWeight: 500,
                    fontSize: '16px',
                    '&:hover': { color: isDarkMode ? '#ffffff' : '#1B4672' },
                  }}
                >
                  Back to Login
                </Button>
              </Typography>

            </Box>
          </Box>
        </Box>
      </Box>

      {/* Success Dialog */}
      <SuccessDialog
        open={showSuccessDialog}
        onClose={() => setShowSuccessDialog(false)}
        title={successDialogConfig.title}
        message={successDialogConfig.message}
        buttonText="Got it"
        autoCloseDelay={successDialogConfig.autoCloseDelay}
      />
    </ThemeProvider>
  );
};

export default ClientSignup; 